import { SizeGuideProvider } from '@app/components/SizeGuide';
import { PTRDataStoreType } from '@app/data-stores/ptr/types';
import { CollapsibleColorPicker } from '@app/pages/i/components/ProductConfigurator/CollapsibleColorPicker';
import { ProductDetailsSection } from '@app/pages/i/components/ProductDetailsSection/ProductDetailsSection';
import { ProductPreview } from '@app/pages/i/components/ProductPreviews';
import { PreviewNavDots } from '@app/pages/i/components/ProductPreviews/PreviewNavDots';
import { SelectedPreviewState } from '@app/pages/i/components/ProductPreviews/types';
import {
  filterPreviews,
  getInitialPreview,
} from '@app/pages/i/components/ProductPreviews/utils';
import { CurrentPreviewProvider } from '@app/pages/i/hooks/useCurrentPreview';
import { usePTRProductConfigurator } from '@app/pages/i/hooks/usePTRProductConfigurator';
import { ProductPageProps } from '@app/pages/i/types';
import { getPTRInventoryProps } from '@app/pages/i/utils/getPTRInventoryProps';
import { useSimilarDesignsContext } from '@app/pages/i/utils/SimilarDesignsContext';
import { InventoryItem } from '@app/types/shop-bff/types';
import { trackGA4AddToCartEvent } from '@app/utils/analytics';
import { SearchUUIDStorageKey } from '@app/utils/searchUUIDStorage';
import Box from '@redbubble/design-system/src/Box';
import { useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import {
  AnalyticsATCContext,
  AnalyticsPageType,
} from '@redbubble/next-analytics/gtm';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import { uniqBy } from 'lodash';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef } from 'react';
import { PDP_LEADING_IMAGE_EXPERIMENT_PRODUCTS } from '../../constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCurrentInventoryStore } from '../../hooks/useCurrentInventoryItem';
import styles from './PrimaryContentSection.module.scss';

interface PrimaryContentSectionProps {
  setAtcDrawerOpen: (open: boolean) => void;
  setConfettiActive: (active: boolean) => void;
  productTypes: PTRDataStoreType['productTypes'];
  productFamilies: PTRDataStoreType['productFamilies'];
}

export const PrimaryContentSection: React.FC<PrimaryContentSectionProps> = ({
  setAtcDrawerOpen,
  setConfettiActive,
  productTypes,
  productFamilies,
}) => {
  useAnalytics();
  const intl = useIntl();
  const locale = intl.locale;
  const {
    inventoryCategorySet,
    defaultInventoryItems,
    isPdpStylePickerExperimentProduct,
  } = usePageProps<ProductPageProps>();
  const [{ currency }] = useUserInfo<UserInfo>();

  const { blankItemsById, inventoryBySelector: inventoryBySelectorProp } =
    getPTRInventoryProps(productTypes, inventoryCategorySet.items);

  const { currentInventoryItem, setCurrentInventoryItem } =
    useCurrentInventoryStore((state) => state);
  const isPdpLeadingImageExperimentProduct =
    PDP_LEADING_IMAGE_EXPERIMENT_PRODUCTS.includes(
      currentInventoryItem.productTypeId
    );
  useAmplitudeExperiment(ExperimentConfig.PdpApparelLeadingImage, {
    skipExposure: !isPdpLeadingImageExperimentProduct,
  });
  const pdpStylePickerEnrolment = useAmplitudeExperiment(
    ExperimentConfig.PdpStylePicker,
    { skipExposure: !isPdpStylePickerExperimentProduct }
  );
  const isPdpStylePickerTreatment =
    isPdpStylePickerExperimentProduct &&
    pdpStylePickerEnrolment === 'treatment';
  const { showOffsiteSimilarDesignsSection } = useSimilarDesignsContext();
  const handleSetCurrentInventoryItem = (inventoryItem: InventoryItem) => {
    if (
      currentInventoryItem.blankItemId === inventoryItem.blankItemId &&
      currentInventoryItem.workId === inventoryItem.workId
    )
      return;
    setCurrentInventoryItem(inventoryItem);
  };
  const initialProductType = useMemo(
    () => productTypes[currentInventoryItem.productTypeId],
    [productTypes, currentInventoryItem.productTypeId]
  );

  const {
    currentProductType,
    currentProductFamily,
    currentValues,
    updateForm,
    showColorConfigSeparately,
    showingFallback,
    colorConfiguratorOptions,
    configuratorOptions,
  } = usePTRProductConfigurator({
    currentInventoryItem,
    setCurrentInventoryItem: handleSetCurrentInventoryItem,
    productTypes,
    productFamilies,
    blankItemsById,
    initialProductType,
    inventoryBySelector: inventoryBySelectorProp,
  });

  const previews = currentInventoryItem?.previewSet?.previews
    ? filterPreviews(currentInventoryItem.previewSet.previews)
    : [];

  const { current: initialPreview } = useRef<SelectedPreviewState>(
    getInitialPreview(previews)
  );
  const { work } = inventoryCategorySet.defaultItem;
  const router = useRouter();
  const firstRender = useRef(true);

  useEffect(() => {
    if (currentInventoryItem?.productPageUrl) {
      // always fire a product view on first render
      if (firstRender.current) {
        firstRender.current = false;
      }

      const currentPageUrl = window.location;
      const productPageUrl = new URL(currentInventoryItem.productPageUrl);

      // Only fire once for each url change
      // this stops an infinite loop of updates
      // due to router being in the dependency array
      if (productPageUrl.pathname === currentPageUrl.pathname) return;

      const { slug } = router.query;

      // Only update URL if on a product page using old BlankItemId based routing
      // this should never happen on a PTUID based URL as each blank will have the same
      // productPageUrl however this is an extra guard
      // this should be removed at the end of the PTUID migration
      if (Array.isArray(slug) && slug.length === 3) {
        router.replace(
          {
            pathname: productPageUrl.pathname,
            search: currentPageUrl.search,
            hash: currentPageUrl.hash,
          },
          undefined,
          { shallow: true }
        );
      }
    }
  }, [currentInventoryItem, router]);

  const isSwatchExpTreatment =
    useAmplitudeExperiment(ExperimentConfig.PdpMobileColorSwatchVisibility) ===
    'treatment';

  // TODO: Fix upstream, this is a workaround to fix https://redbubble.atlassian.net/browse/DEP-3623

  const uniquePreviews = useMemo(() => {
    return uniqBy(previews, 'url');
  }, [previews]);

  return (
    <Box
      className={[
        styles.primaryContent,
        showOffsiteSimilarDesignsSection ? styles.offsitePrimaryContent : '',
        isPdpStylePickerTreatment ? styles.pdpStylePickerTreatment : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="primary-content"
    >
      <CurrentPreviewProvider initialPreview={initialPreview}>
        <Box className={styles.productPreviewsLayout}>
          <Box className={styles.preview}>
            {currentInventoryItem?.previewSet?.previews &&
              currentInventoryItem?.previewSet.previews.length > 0 && (
                <ProductPreview
                  currentInventoryItem={currentInventoryItem}
                  previews={uniquePreviews}
                  artistName={work?.artistShopDisplayName ?? undefined}
                  workTitle={work?.title}
                  productCategoryName={inventoryCategorySet.categoryName}
                  matureContent={work?.matureContent}
                  initialPreview={initialPreview}
                />
              )}
          </Box>
        </Box>

        <Box
          className={
            showColorConfigSeparately
              ? styles.mobilePreviewActionsColorPicker
              : styles.mobilePreviewActions
          }
        >
          <Box className={styles.mobilePreviewActionsGrid}>
            <PreviewNavDots
              previews={uniquePreviews}
              showColorConfigSeparately={
                showColorConfigSeparately && !isSwatchExpTreatment
              }
            />

            {/* XXX: This currently can render more than one color picker field
                which will result in a visually broken experience.
                No current products have more than one color picker however
                we may want to consider how to handle this in the future should we
                want to introduce this. It would be best to do this via the data
                provided rather than by slicing arrays */}
            {showColorConfigSeparately &&
              currentProductType &&
              colorConfiguratorOptions.map((colorConfiguratorOption) => (
                <CollapsibleColorPicker
                  productType={currentProductType}
                  currentValues={currentValues}
                  onConfiguratorFormChange={updateForm}
                  locale={locale}
                  colorConfiguratorOption={colorConfiguratorOption}
                />
              ))}
          </Box>
        </Box>
      </CurrentPreviewProvider>

      <SizeGuideProvider
        productTypeId={currentInventoryItem.productTypeId}
        workId={currentInventoryItem.workId}
        inventoryItemId={currentInventoryItem.id}
        locale={locale}
        currency={currency}
        hasSizing={!!initialProductType?.sizing}
      >
        <ProductDetailsSection
          configuratorOptions={configuratorOptions}
          currentInventoryItem={currentInventoryItem}
          inventoryCategorySet={inventoryCategorySet}
          availableProducts={defaultInventoryItems}
          productType={currentProductType}
          productFamily={currentProductFamily}
          currentValues={currentValues}
          updateForm={updateForm}
          showColorConfigSeparately={showColorConfigSeparately}
          showingFallback={showingFallback}
          isPdpStylePickerTreatment={isPdpStylePickerTreatment}
          locale={locale}
          onAddToCart={() => {
            trackGA4AddToCartEvent({
              item: currentInventoryItem,
              workId: `${currentInventoryItem.workId}`,
              pageType: AnalyticsPageType.ProductDetailsPage,
              context: AnalyticsATCContext.ProductDetailsPage,
              searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
            });
            setAtcDrawerOpen(true);
            setConfettiActive(true);
          }}
        />
      </SizeGuideProvider>
    </Box>
  );
};
